/**
 * My record — my flags, and every clock-in away from the office.
 * Route: /record
 * @file src/web/features/record/pages/index.tsx
 *
 * @llm-rule WHEN: Someone wants to see what is on their own file
 * @llm-rule NOTE: Flags are raised by rule, never typed — so this page can
 *   state exactly which rule fired. Appreciations are gone: nothing created
 *   them once manual remarks were removed.
 */

import { useCallback, useEffect, useState } from 'react';
import { Skeleton, Tabs, TabsContent, TabsList, TabsTrigger, toast } from '@bloomneo/uikit';
import { Flag, MapPin } from 'lucide-react';
import { AuthGuard } from '../../auth';
import { AppShell } from '../../../shared/components/AppShell';
import { Panel } from '../../../shared/components/presence';
import { useAppRequest } from '../../../shared/hooks/useAppApi';
import { formatClock, formatDistance, OFFSITE_REASON_LABEL } from '../../../shared/domain';
import { FLAG_LABEL, FLAG_RULE, type FlagCode } from '../../../shared/flags';

interface Remark {
  id: string;
  type: 'APPRECIATION' | 'FLAG';
  code: FlagCode | null;
  body: string;
  day: string;
  status: string;
  createdAt: string;
  replies: Array<{ id: string; body: string; createdAt: string; author: { name: string | null } }>;
}

interface OffsiteRow {
  id: string;
  day: string;
  clockInAt: string;
  approvalStatus: string;
  distanceM: number | null;
  locationName: string | null;
  offsiteReason: string | null;
  offsiteNote: string | null;
  decisionNote: string | null;
  decidedBy: { name: string | null } | null;
}

function dayLabel(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  });
}

/**
 * MODULE level, deliberately — see never-nesting note in AGENTS.
 *
 * There is no reply thread here any more. A flag is raised by rule and either
 * stands or is waived by a Head or the Company Admin; arguing it out inside the app added
 * a second, slower place to have a conversation people were already having
 * elsewhere.
 */
function FlagList({ rows }: { rows: Remark[] }) {
  if (rows.length === 0) {
    return (
      <p className="px-5 py-10 text-center text-[13px] text-ink-faint">
        No flags. Everything on time, on site, and on schedule.
      </p>
    );
  }

  return (
    <ul className="divide-y divide-rule">
      {rows.map((row) => (
        <li key={row.id} className="px-5 py-4">
          <div className="border-l-[3px] border-flag pl-3.5">
            <div className="flex flex-wrap items-baseline gap-2">
              <span className="text-[13px] font-semibold text-ink">
                {row.code ? FLAG_LABEL[row.code] : 'Flag'}
              </span>
              <span className="text-[11.5px] text-ink-ghost">· {dayLabel(row.day)}</span>
              <span className="rounded-full bg-paper-sunk px-2 py-0.5 text-[10.5px] font-medium text-ink-faint">
                {row.status.toLowerCase()}
              </span>
            </div>

            {row.code && (
              <p className="mt-0.5 text-[11px] text-ink-ghost">{FLAG_RULE[row.code]}</p>
            )}

            <p className="mt-1 text-[13px] leading-relaxed text-ink">{row.body}</p>

            {/* Nothing to press. A flag is cleared by a waiver from a Head or
                your own Manager, never by the person it is about. */}
            {row.status === 'RESOLVED' && (
              <p className="mt-2 text-[11.5px] text-ink-ghost">Waived</p>
            )}
          </div>
        </li>
      ))}
    </ul>
  );
}

function RecordPage() {
  const { request } = useAppRequest();

  const [flags, setFlags] = useState<Remark[]>([]);
  const [offsite, setOffsite] = useState<OffsiteRow[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    const [remarkResult, offsiteResult] = await Promise.all([
      request<{ remarks: Remark[] }>('/api/remark/mine'),
      request<{ offsite: OffsiteRow[] }>('/api/attendance/offsite/mine'),
    ]);

    if (remarkResult.ok) setFlags(remarkResult.data.remarks.filter((r) => r.type === 'FLAG'));
    else toast.error(remarkResult.error);

    if (offsiteResult.ok) setOffsite(offsiteResult.data.offsite);
    setLoading(false);
  }, [request]);

  useEffect(() => {
    void load();
  }, [load]);



  const open = flags.filter((f) => f.status === 'OPEN').length;

  return (
    <AppShell
      wide
      title="My flags"
      subtitle={
        open === 0
          ? 'Nothing open against you.'
          : `${open} open flag${open === 1 ? '' : 's'}. A Head or the Company Admin can waive one if there was a good reason.`
      }
    >
      {loading ? (
        <Skeleton className="h-80 w-full rounded-[18px]" />
      ) : (
        <div className="mx-auto max-w-3xl">
          <Tabs defaultValue="flags">
            <TabsList>
              <TabsTrigger value="flags">
                <Flag className="mr-1.5 size-3.5" />
                Flags
                <span className="ml-1.5 rounded-full bg-[var(--state-bad-bg)] px-1.5 text-[10.5px] font-semibold text-flag">
                  {flags.length}
                </span>
              </TabsTrigger>
              <TabsTrigger value="offsite">
                <MapPin className="mr-1.5 size-3.5" />
                Off-site logins
                <span className="ml-1.5 rounded-full bg-paper-sunk px-1.5 text-[10.5px] font-semibold text-ink-faint">
                  {offsite.length}
                </span>
              </TabsTrigger>
            </TabsList>

            <TabsContent value="flags" className="pt-4">
              <Panel padded={false} className="overflow-hidden">
                <FlagList rows={flags} />
              </Panel>
            </TabsContent>

            <TabsContent value="offsite" className="pt-4">
              <Panel padded={false} className="overflow-hidden">
                {offsite.length === 0 ? (
                  <p className="px-5 py-10 text-center text-[13px] text-ink-faint">
                    You haven’t clocked in from outside the office.
                  </p>
                ) : (
                  <ul className="divide-y divide-rule">
                    {offsite.map((row) => {
                      // No status pill — nobody signs these off any more.
                      return (
                        <li key={row.id} className="flex flex-wrap items-start gap-3 px-5 py-4">
                          <MapPin className="mt-0.5 size-4 flex-none text-ink-faint" />
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-baseline gap-2">
                              <span className="text-[13px] font-medium text-ink">
                                {dayLabel(row.day)}
                              </span>
                              <span className="text-[11.5px] text-ink-faint">
                                in {formatClock(row.clockInAt)} · {formatDistance(row.distanceM)} from{' '}
                                {row.locationName ?? 'the office'}
                              </span>
                            </div>

                            <p className="mt-1 text-[12.5px] text-ink-soft">
                              {row.offsiteReason ? OFFSITE_REASON_LABEL[row.offsiteReason] : 'Off-site'}
                              {row.offsiteNote ? ` — “${row.offsiteNote}”` : ''}
                            </p>

                            {row.decidedBy && (
                              <p className="mt-0.5 text-[11.5px] text-ink-faint">
                                Decided by {row.decidedBy.name}
                                {row.decisionNote ? ` — “${row.decisionNote}”` : ''}
                              </p>
                            )}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </Panel>
            </TabsContent>
          </Tabs>
        </div>
      )}
    </AppShell>
  );
}

export default function RecordRoute() {
  return (
    <AuthGuard>
      <RecordPage />
    </AuthGuard>
  );
}
